import { useEffect, useMemo, useState, useRef } from 'react';
import useStateFromProps from '@/hooks/useStateFromProps';
import { SVGPureIcon } from '@/ui';
import knowledgeModelSelectStore from './store';
import { Dropdown } from 'antd';
import { observer } from 'mobx-react-lite';
import chatKnowledgeStore from '../../store';
import iconRadioNormal from '@/assets/img/radio_normal.png';
import iconRadioActive from '@/assets/img/radio_active.png';
import { AssistantType, ModelType } from '@shared/constants/chat';
import Loader from '@/common/helper/loader';

const KnowledgeModelSelect = observer((props) => {
  const {
    hideTitle = false,
  } = props;

  const chatStore = chatKnowledgeStore.chatStore;
  const currAsstId = chatStore?.currAsstId;
  const [popupVisible, setPopupVisible] = useState(false);
  const modelInfo = knowledgeModelSelectStore.modelInfo;

  useEffect(() => {
    if(currAsstId && currAsstId != knowledgeModelSelectStore.curId){
      knowledgeModelSelectStore.whenInitReady(() => {
        knowledgeModelSelectStore.switchCurId(currAsstId);
      });
    }
  }, [currAsstId]);

  if (!modelInfo) {
    return null;
  }

  return (
    <Dropdown
      trigger="click"
      placement="topLeft"
      open={popupVisible}
      onOpenChange={open => setPopupVisible(open)}
      dropdownRender={() => (
        <DropdownList
          items={knowledgeModelSelectStore.list}
          activeKey={modelInfo.kn_custom_model?.id || modelInfo.id}
          onItemClick={item => {
            setPopupVisible(false);
            const ids = [item.id];
            if(item.kn_custom_model){
              ids.push(item.kn_custom_model.id);
            }
            knowledgeModelSelectStore.switchCurId(ids.join("_"));
          }}
        />
      )}
    >
      <span
        className={[
          'cursor-pointer',
          'select-none',
          'rounded-full',
          'pl-10px pr-10px py-[6px]',
          'flex items-center gap-[8px] max-w-200px',
          'bg-gray-nav hover:bg-gray-nav-hover',
          'flex-shrink-0',
        ]}
      >
        <div className={`relative size-20px rounded-8px overflow-hidden flex-shrink-0`}>
          <img
            className="size-20px rounded-full"
            draggable={false}
            src={modelInfo.dl_icon ||  modelInfo.icon || Loader.defaultModelIcon}
          />
        </div>
        {
          !hideTitle
          ? (
            <span className="text-[14px] text-primary flex-shrink truncate">
              {modelInfo.title}
            </span>
          )
          : null
        }
        <SVGPureIcon className='w-12px h-8px flex-shrink-0 opacity-50' name="down" />
      </span>
    </Dropdown>
  );
})

function DropdownList(props) {
  const { popupVisible, items = [], onItemClick } = props;
  const listRef = useRef(null);
  const selectedRef = useRef(null);
  const [activeKey, setActiveKey] = useStateFromProps({
    value: props.activeKey,
  });

  useEffect(() => {
    if (popupVisible) {
      const { scrollTop: listScrollTop, offsetHeight: listHeight } =
        listRef.current;
      const { offsetTop: itemOffsetTop, offsetHeight: itemHeight } =
        selectedRef.current;

      if (
        itemOffsetTop + itemHeight < listScrollTop ||
        itemOffsetTop > listScrollTop + listHeight
      ) {
        selectedRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [popupVisible]);

  return (
    <div
      className={[
        'w-260px',
        'bg-white',
        'rounded-16px',
        'pl-8px pr-4px pb-8px',
        'flex flex-col items-center',
      ]}
      style={{ boxShadow: '0px 0px 8px 0px rgba(0,0,0,0.1)' }}
    >
      <div className="h-48px font-bold pl-8px text-black flex items-center flex-shrink-0 w-full">
        选择大模型
      </div>
      <div className="w-228px h-1px bg-black bg-opacity-6 mb-8px"></div>
      <ul
        ref={listRef}
        className={[
          'overflow-y-scroll nw-scrollbar-thin w-full',
          'flex flex-col gap-[4px] m-0 max-h-160px',
        ]}
      >
        {items.map((item) => {
          const { id:model_id, title, icon, dl_icon, chat_type, model_type, kn_custom_model } = item;
          const id = kn_custom_model?.id || model_id;

          return (
            <li
              key={id}
              ref={activeKey === id ? selectedRef : null}
              className={[
                'rounded-[12px]',
                'p-[8px]',
                'flex items-center',
                'hover:bg-[#F9FAFB]',
                'cursor-pointer w-full',
              ]}
              onClick={() => {
                setActiveKey(id);
                onItemClick?.(item);
              }}
            >
              <img src={activeKey === id ? iconRadioActive : iconRadioNormal} className="size-16px mr-10px" />
              <img className="size-20px rounded-full border-1px border-black border-opacity-10 border-solid mr-6px" src={dl_icon || icon || Loader.defaultModelIcon} />
              <span className="select-none text-[14px] text-[#222] font-medium flex-shrink truncate mr-6px">
                {title}
              </span>
              {chat_type === AssistantType.LocalModel ? (
                <span
                  className={`flex-shrink-0 h-18px w-36px flex items-center justify-center font-bold mr-6px rounded-4px text-12px bg-[#0058FF] bg-opacity-10 text-[#0058FF]`}
                >
                  本地
                </span>
              ) : null}
              {model_type === ModelType.APIModel && (
                <span
                  className={`flex-shrink-0 h-18px w-36px flex items-center justify-center font-bold mr-6px rounded-4px text-12px bg-[#E43F9F] bg-opacity-10 text-[#E43F9F]`}
                >
                  API
                </span>
              )}
              {model_type === ModelType.PrivateModel && (
                <span
                  className={`flex-shrink-0 h-18px w-36px flex items-center justify-center font-bold mr-6px rounded-4px text-12px bg-[#FF7307] bg-opacity-10 text-[#FF7307]`}
                >
                  私有
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  );
}

export default KnowledgeModelSelect;
